import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { useAppScaffolder, ScaffoldResult } from "../../../../hooks/useAppScaffolder";

interface Props {
  show: boolean;
  handleClose: () => void;
  projectId: string;
  projectTitle: string;
}

type AppType = "expo" | "nextjs" | "both";
type Step = "template" | "platform" | "features" | "review" | "scaffolding" | "complete";
type AppTemplate = "directory" | "ecommerce" | "content" | "social" | "custom";
type DataSource = "airtable" | "firebase" | "supabase" | "custom";
type NavigationStyle = "tabs" | "drawer" | "stack";

interface Features {
  maps: boolean;
  location: boolean;
  auth: boolean;
  favorites: boolean;
  search: boolean;
  notifications: boolean;
  analytics: boolean;
  camera: boolean;
}

interface AppConfig {
  template: AppTemplate;
  dataSource: DataSource;
  navigationStyle: NavigationStyle;
  features: Features;
}

const TEMPLATES = [
  {
    id: "directory" as AppTemplate,
    label: "Directory / Places",
    desc: "Like Bark Around Ireland - locations, maps, listings",
    icon: "bi-geo-alt",
    defaultFeatures: { maps: true, location: true, favorites: true, search: true },
  },
  {
    id: "ecommerce" as AppTemplate,
    label: "E-commerce",
    desc: "Products, cart, checkout, orders",
    icon: "bi-bag",
    defaultFeatures: { auth: true, search: true, notifications: true },
  },
  {
    id: "content" as AppTemplate,
    label: "Content / Blog",
    desc: "Articles, news, media feed",
    icon: "bi-journal-text",
    defaultFeatures: { search: true, favorites: true },
  },
  {
    id: "social" as AppTemplate,
    label: "Social / Community",
    desc: "Profiles, posts, messaging",
    icon: "bi-people",
    defaultFeatures: { auth: true, camera: true, notifications: true },
  },
  {
    id: "custom" as AppTemplate,
    label: "Custom / Blank",
    desc: "Start from scratch, pick your own features",
    icon: "bi-puzzle",
    defaultFeatures: {},
  },
];

const DATA_SOURCES = [
  { id: "airtable" as DataSource, label: "Airtable", desc: "No-code database, easy to setup", icon: "bi-table" },
  { id: "firebase" as DataSource, label: "Firebase", desc: "Google's real-time database", icon: "bi-database" },
  { id: "supabase" as DataSource, label: "Supabase", desc: "Open source Firebase alternative", icon: "bi-lightning" },
  { id: "custom" as DataSource, label: "Custom API", desc: "Your own backend/API", icon: "bi-code-slash" },
];

const NAV_STYLES = [
  { id: "tabs" as NavigationStyle, label: "Tab Bar", desc: "Bottom tabs (3-5 screens)", icon: "bi-segmented-nav" },
  { id: "drawer" as NavigationStyle, label: "Drawer", desc: "Side menu navigation", icon: "bi-list" },
  { id: "stack" as NavigationStyle, label: "Stack Only", desc: "Simple back/forward flow", icon: "bi-layers" },
];

const ALL_FEATURES = [
  { key: "maps", label: "Maps", desc: "Google/Apple Maps integration", icon: "bi-map" },
  { key: "location", label: "Location", desc: "GPS & nearby search", icon: "bi-geo" },
  { key: "auth", label: "Authentication", desc: "User login & signup", icon: "bi-person-lock" },
  { key: "favorites", label: "Favorites", desc: "Save & bookmark items", icon: "bi-heart" },
  { key: "search", label: "Search & Filters", desc: "Find & filter content", icon: "bi-search" },
  { key: "notifications", label: "Push Notifications", desc: "Alerts & updates", icon: "bi-bell" },
  { key: "analytics", label: "Analytics", desc: "Track user behavior", icon: "bi-graph-up" },
  { key: "camera", label: "Camera", desc: "Photos & uploads", icon: "bi-camera" },
];

export const MVPScaffolderModal: React.FC<Props> = ({
  show,
  handleClose,
  projectId,
  projectTitle,
}) => {
  const [currentStep, setCurrentStep] = useState<Step>("template");
  const [appType, setAppType] = useState<AppType>("expo");
  const [config, setConfig] = useState<AppConfig>({
    template: "directory",
    dataSource: "airtable",
    navigationStyle: "tabs",
    features: {
      maps: true,
      location: true,
      auth: false,
      favorites: true,
      search: true,
      notifications: false,
      analytics: false,
      camera: false,
    },
  });
  const [copied, setCopied] = useState<string | null>(null);

  const {
    isScaffolding,
    error,
    expoResult,
    nextResult,
    scaffoldExpoApp,
    scaffoldNextApp,
    clearError,
    reset,
  } = useAppScaffolder();

  useEffect(() => {
    if (show) {
      reset();
      setCurrentStep("template");
      setAppType("expo");
      setConfig({
        template: "directory",
        dataSource: "airtable",
        navigationStyle: "tabs",
        features: {
          maps: true,
          location: true,
          auth: false,
          favorites: true,
          search: true,
          notifications: false,
          analytics: false,
          camera: false,
        },
      });
      setCopied(null);
    }
  }, [show, reset]);

  const handleTemplateSelect = (template: AppTemplate) => {
    const templateConfig = TEMPLATES.find((t) => t.id === template);
    const newFeatures = { ...config.features };

    // Reset all features
    Object.keys(newFeatures).forEach((key) => {
      newFeatures[key as keyof Features] = false;
    });

    // Apply template defaults
    if (templateConfig?.defaultFeatures) {
      Object.entries(templateConfig.defaultFeatures).forEach(([key, value]) => {
        if (key in newFeatures) {
          newFeatures[key as keyof Features] = value as boolean;
        }
      });
    }

    setConfig({ ...config, template, features: newFeatures });
  };

  const handleScaffold = async () => {
    setCurrentStep("scaffolding");

    const scaffoldConfig = {
      template: config.template,
      dataSource: config.dataSource,
      navigationStyle: config.navigationStyle,
      features: Object.entries(config.features)
        .filter(([, enabled]) => enabled)
        .map(([name]) => name),
    };

    let success = false;

    if (appType === "expo" || appType === "both") {
      const result = await scaffoldExpoApp(projectId, scaffoldConfig);
      success = !!result;
    }

    if ((appType === "nextjs" || appType === "both") && (appType !== "both" || success)) {
      const result = await scaffoldNextApp(projectId, scaffoldConfig);
      success = !!result;
    }

    if (success) {
      setCurrentStep("complete");
    } else {
      setCurrentStep("review");
    }
  };

  const handleCopyCommand = (command: string, type: string) => {
    navigator.clipboard.writeText(command);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const getStepNumber = () => {
    switch (currentStep) {
      case "template": return 1;
      case "platform": return 2;
      case "features": return 3;
      case "review": return 4;
      case "scaffolding":
      case "complete": return 5;
      default: return 1;
    }
  };

  const stepLabels = ["Template", "Platform", "Features", "Review", "Complete"];

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bolder">
          <i className="bi bi-magic me-2 text-primary"></i>
          MVP Builder
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-3">
        {error && (
          <div className="alert alert-danger mb-4 d-flex align-items-center">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <span className="flex-grow-1">{error}</span>
            <button type="button" className="btn-close" onClick={clearError}></button>
          </div>
        )}

        {/* Progress Steps */}
        <div className="d-flex justify-content-between mb-4 px-2">
          {stepLabels.map((label, index) => (
            <div key={label} className="d-flex flex-column align-items-center" style={{ flex: 1 }}>
              <div className="d-flex align-items-center w-100">
                {index > 0 && (
                  <div
                    className={`flex-grow-1 ${index < getStepNumber() ? "bg-primary" : "bg-light"}`}
                    style={{ height: "2px" }}
                  />
                )}
                <div
                  className={`d-flex align-items-center justify-content-center rounded-circle ${
                    index + 1 <= getStepNumber() ? "bg-primary text-white" : "bg-light text-muted"
                  }`}
                  style={{ width: "32px", height: "32px", minWidth: "32px" }}
                >
                  {index + 1 < getStepNumber() ? <i className="bi bi-check"></i> : index + 1}
                </div>
                {index < stepLabels.length - 1 && (
                  <div
                    className={`flex-grow-1 ${index + 1 < getStepNumber() ? "bg-primary" : "bg-light"}`}
                    style={{ height: "2px" }}
                  />
                )}
              </div>
              <span className={`fs-8 mt-1 ${index + 1 <= getStepNumber() ? "text-dark" : "text-muted"}`}>
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* Step 1: Template Selection */}
        {currentStep === "template" && (
          <div className="py-3">
            <h5 className="fw-bold text-center mb-4">What kind of app are you building?</h5>
            <div className="row g-3">
              {TEMPLATES.map((template) => (
                <div key={template.id} className="col-md-4 col-6">
                  <label
                    className={`d-flex flex-column align-items-center p-4 rounded cursor-pointer border h-100 text-center ${
                      config.template === template.id ? "border-primary bg-light-primary" : "border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="template"
                      value={template.id}
                      checked={config.template === template.id}
                      onChange={() => handleTemplateSelect(template.id)}
                      className="d-none"
                    />
                    <i className={`bi ${template.icon} fs-1 mb-2 ${
                      config.template === template.id ? "text-primary" : "text-gray-500"
                    }`}></i>
                    <span className="fw-bold mb-1">{template.label}</span>
                    <span className="text-muted fs-8">{template.desc}</span>
                  </label>
                </div>
              ))}
            </div>

            {/* Data Source Selection */}
            <h6 className="fw-bold mt-5 mb-3">
              <i className="bi bi-database me-2"></i>
              Where will your data live?
            </h6>
            <div className="row g-2">
              {DATA_SOURCES.map((ds) => (
                <div key={ds.id} className="col-md-3 col-6">
                  <label
                    className={`d-flex align-items-center p-3 rounded cursor-pointer border ${
                      config.dataSource === ds.id ? "border-primary bg-light-primary" : "border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="dataSource"
                      value={ds.id}
                      checked={config.dataSource === ds.id}
                      onChange={() => setConfig({ ...config, dataSource: ds.id })}
                      className="d-none"
                    />
                    <i className={`bi ${ds.icon} fs-4 me-2 ${
                      config.dataSource === ds.id ? "text-primary" : "text-gray-500"
                    }`}></i>
                    <div>
                      <span className="fw-bold d-block fs-7">{ds.label}</span>
                      <span className="text-muted fs-8">{ds.desc}</span>
                    </div>
                  </label>
                </div>
              ))}
            </div>

            {/* Navigation Style */}
            <h6 className="fw-bold mt-4 mb-3">
              <i className="bi bi-signpost-2 me-2"></i>
              Navigation Style
            </h6>
            <div className="row g-2">
              {NAV_STYLES.map((nav) => (
                <div key={nav.id} className="col-md-4">
                  <label
                    className={`d-flex align-items-center p-3 rounded cursor-pointer border ${
                      config.navigationStyle === nav.id ? "border-primary bg-light-primary" : "border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="navStyle"
                      value={nav.id}
                      checked={config.navigationStyle === nav.id}
                      onChange={() => setConfig({ ...config, navigationStyle: nav.id })}
                      className="d-none"
                    />
                    <i className={`bi ${nav.icon} fs-4 me-2 ${
                      config.navigationStyle === nav.id ? "text-primary" : "text-gray-500"
                    }`}></i>
                    <div>
                      <span className="fw-bold d-block fs-7">{nav.label}</span>
                      <span className="text-muted fs-8">{nav.desc}</span>
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Platform Selection */}
        {currentStep === "platform" && (
          <div className="py-4">
            <h5 className="fw-bold text-center mb-4">Choose your platform</h5>
            <div className="row g-4 px-4 justify-content-center">
              <div className="col-md-4">
                <label
                  className={`d-flex flex-column align-items-center p-4 rounded cursor-pointer border h-100 ${
                    appType === "expo" ? "border-primary bg-light-primary" : "border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="appType"
                    value="expo"
                    checked={appType === "expo"}
                    onChange={() => setAppType("expo")}
                    className="d-none"
                  />
                  <i className={`bi bi-phone fs-1 mb-3 ${appType === "expo" ? "text-primary" : "text-gray-500"}`}></i>
                  <span className="fw-bold fs-5 mb-1">Mobile App</span>
                  <span className="text-muted fs-7 text-center">
                    Expo React Native<br />iOS & Android
                  </span>
                  <span className="badge bg-success mt-2">Recommended</span>
                </label>
              </div>
              <div className="col-md-4">
                <label
                  className={`d-flex flex-column align-items-center p-4 rounded cursor-pointer border h-100 ${
                    appType === "nextjs" ? "border-primary bg-light-primary" : "border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="appType"
                    value="nextjs"
                    checked={appType === "nextjs"}
                    onChange={() => setAppType("nextjs")}
                    className="d-none"
                  />
                  <i className={`bi bi-globe fs-1 mb-3 ${appType === "nextjs" ? "text-primary" : "text-gray-500"}`}></i>
                  <span className="fw-bold fs-5 mb-1">Web App</span>
                  <span className="text-muted fs-7 text-center">
                    Next.js 14<br />React & Tailwind
                  </span>
                </label>
              </div>
              <div className="col-md-4">
                <label
                  className={`d-flex flex-column align-items-center p-4 rounded cursor-pointer border h-100 ${
                    appType === "both" ? "border-primary bg-light-primary" : "border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="appType"
                    value="both"
                    checked={appType === "both"}
                    onChange={() => setAppType("both")}
                    className="d-none"
                  />
                  <i className={`bi bi-layers fs-1 mb-3 ${appType === "both" ? "text-primary" : "text-gray-500"}`}></i>
                  <span className="fw-bold fs-5 mb-1">Both</span>
                  <span className="text-muted fs-7 text-center">
                    Mobile + Web<br />Full stack
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Features */}
        {currentStep === "features" && (
          <div className="py-3">
            <h5 className="fw-bold text-center mb-4">Select features to include</h5>
            <div className="row g-3">
              {ALL_FEATURES.map((feature) => (
                <div key={feature.key} className="col-md-6 col-lg-4">
                  <label
                    className={`d-flex align-items-center p-3 rounded cursor-pointer border ${
                      config.features[feature.key as keyof Features]
                        ? "border-primary bg-light-primary"
                        : "border-gray-300"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={config.features[feature.key as keyof Features]}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          features: { ...config.features, [feature.key]: e.target.checked },
                        })
                      }
                      className="form-check-input me-3"
                    />
                    <i className={`bi ${feature.icon} fs-3 me-3 ${
                      config.features[feature.key as keyof Features] ? "text-primary" : "text-gray-500"
                    }`}></i>
                    <div>
                      <span className="fw-bold d-block">{feature.label}</span>
                      <span className="text-muted fs-8">{feature.desc}</span>
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {currentStep === "review" && (
          <div className="py-3">
            <h5 className="fw-bold text-center mb-4">Review your configuration</h5>

            <div className="row g-4">
              <div className="col-md-6">
                <div className="bg-light rounded p-4 h-100">
                  <h6 className="fw-bold text-primary mb-3">
                    <i className="bi bi-puzzle me-2"></i>Template
                  </h6>
                  <div className="d-flex align-items-center mb-3">
                    <i className={`bi ${TEMPLATES.find((t) => t.id === config.template)?.icon} fs-3 me-2 text-primary`}></i>
                    <span className="fw-bold">{TEMPLATES.find((t) => t.id === config.template)?.label}</span>
                  </div>

                  <h6 className="fw-bold text-primary mb-2 mt-4">
                    <i className="bi bi-box me-2"></i>Platform
                  </h6>
                  <span className="fw-bold">
                    {appType === "expo" ? "Mobile (Expo)" : appType === "nextjs" ? "Web (Next.js)" : "Mobile + Web"}
                  </span>

                  <h6 className="fw-bold text-primary mb-2 mt-4">
                    <i className="bi bi-database me-2"></i>Data Source
                  </h6>
                  <span className="fw-bold">{DATA_SOURCES.find((d) => d.id === config.dataSource)?.label}</span>

                  <h6 className="fw-bold text-primary mb-2 mt-4">
                    <i className="bi bi-signpost-2 me-2"></i>Navigation
                  </h6>
                  <span className="fw-bold">{NAV_STYLES.find((n) => n.id === config.navigationStyle)?.label}</span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="bg-light rounded p-4 h-100">
                  <h6 className="fw-bold text-primary mb-3">
                    <i className="bi bi-gear me-2"></i>Features
                  </h6>
                  <div className="d-flex flex-wrap gap-2 mb-4">
                    {Object.entries(config.features)
                      .filter(([, enabled]) => enabled)
                      .map(([name]) => (
                        <span key={name} className="badge bg-primary text-capitalize">{name}</span>
                      ))}
                    {!Object.values(config.features).some((v) => v) && (
                      <span className="text-muted">No features selected</span>
                    )}
                  </div>

                  <h6 className="fw-bold text-primary mb-3">
                    <i className="bi bi-info-circle me-2"></i>What will be created
                  </h6>
                  <ul className="list-unstyled mb-0 fs-7">
                    <li className="mb-2">
                      <i className="bi bi-check2 text-success me-2"></i>
                      GitHub repo in businessfactory-apps org
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check2 text-success me-2"></i>
                      Pre-configured with your branding
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check2 text-success me-2"></i>
                      EAS build config (eas.json)
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check2 text-success me-2"></i>
                      TypeScript + navigation setup
                    </li>
                    <li className="mb-0">
                      <i className="bi bi-check2 text-success me-2"></i>
                      Setup instructions & README
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Scaffolding */}
        {currentStep === "scaffolding" && (
          <div className="py-5 text-center">
            <div className="spinner-border text-primary mb-4" style={{ width: "3rem", height: "3rem" }}>
              <span className="visually-hidden">Scaffolding...</span>
            </div>
            <h5 className="fw-bold mb-2">Creating your MVP...</h5>
            <p className="text-muted">Setting up repository, applying branding, configuring features...</p>
          </div>
        )}

        {/* Step 6: Complete */}
        {currentStep === "complete" && (
          <div className="py-3">
            <div className="text-center mb-4">
              <div
                className="d-inline-flex align-items-center justify-content-center bg-success rounded-circle mb-3"
                style={{ width: "64px", height: "64px" }}
              >
                <i className="bi bi-check-lg text-white fs-2"></i>
              </div>
              <h5 className="fw-bold mb-1">MVP Created Successfully!</h5>
              <p className="text-muted">Your app is ready to clone and run.</p>
            </div>

            {expoResult && (
              <ResultCard
                title="Mobile App (Expo)"
                result={expoResult}
                icon="bi-phone"
                onCopy={handleCopyCommand}
                copied={copied === "expo"}
              />
            )}

            {nextResult && (
              <ResultCard
                title="Web App (Next.js)"
                result={nextResult}
                icon="bi-globe"
                onCopy={handleCopyCommand}
                copied={copied === "nextjs"}
              />
            )}

            {/* Enhanced Next Steps */}
            <div className="card border-primary mt-4">
              <div className="card-header bg-primary text-white">
                <h6 className="mb-0 fw-bold">
                  <i className="bi bi-rocket-takeoff me-2"></i>
                  Getting Started Guide
                </h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="fw-bold mb-3">1. Clone & Setup</h6>
                    <ol className="fs-7 mb-0 ps-3">
                      <li className="mb-1">Clone the repository</li>
                      <li className="mb-1">Run <code>npm install</code></li>
                      <li className="mb-1">Copy <code>.env.example</code> to <code>.env</code></li>
                      <li className="mb-1">Add your API keys</li>
                    </ol>
                  </div>
                  <div className="col-md-6">
                    <h6 className="fw-bold mb-3">2. Test Locally</h6>
                    <ol className="fs-7 mb-0 ps-3">
                      <li className="mb-1">Run <code>npx expo start</code></li>
                      <li className="mb-1">Download Expo Go on your phone</li>
                      <li className="mb-1">Scan the QR code</li>
                      <li className="mb-1">Test your app!</li>
                    </ol>
                  </div>
                </div>

                <hr />

                <div className="row">
                  <div className="col-md-6">
                    <h6 className="fw-bold mb-3">3. Build for TestFlight</h6>
                    <ol className="fs-7 mb-0 ps-3">
                      <li className="mb-1">Run <code>eas login</code></li>
                      <li className="mb-1">Run <code>eas build --profile preview --platform ios</code></li>
                      <li className="mb-1">Wait for build (~15 min)</li>
                      <li className="mb-1">Install via TestFlight link</li>
                    </ol>
                  </div>
                  <div className="col-md-6">
                    <h6 className="fw-bold mb-3">4. Submit to App Store</h6>
                    <ol className="fs-7 mb-0 ps-3">
                      <li className="mb-1">Run <code>eas build --profile production --platform ios</code></li>
                      <li className="mb-1">Run <code>eas submit --platform ios</code></li>
                      <li className="mb-1">Complete App Store listing</li>
                      <li className="mb-1">Submit for review</li>
                    </ol>
                  </div>
                </div>

                <div className="bg-light rounded p-3 mt-3">
                  <h6 className="fw-bold mb-2">
                    <i className="bi bi-key me-2"></i>
                    Required Accounts
                  </h6>
                  <div className="d-flex flex-wrap gap-3 fs-7">
                    <span><i className="bi bi-check-circle text-success me-1"></i> Expo account (free)</span>
                    <span><i className="bi bi-check-circle text-success me-1"></i> Apple Developer ($99/yr)</span>
                    <span><i className="bi bi-check-circle text-success me-1"></i> Google Play ($25 once)</span>
                    {config.dataSource === "airtable" && (
                      <span><i className="bi bi-check-circle text-success me-1"></i> Airtable (free tier)</span>
                    )}
                    {config.dataSource === "firebase" && (
                      <span><i className="bi bi-check-circle text-success me-1"></i> Firebase (free tier)</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        {currentStep !== "scaffolding" && currentStep !== "complete" && (
          <button type="button" className="btn btn-light" onClick={handleClose}>
            Cancel
          </button>
        )}

        {currentStep === "template" && (
          <button type="button" className="btn btn-primary" onClick={() => setCurrentStep("platform")}>
            Continue <i className="bi bi-arrow-right ms-2"></i>
          </button>
        )}

        {currentStep === "platform" && (
          <>
            <button type="button" className="btn btn-light" onClick={() => setCurrentStep("template")}>
              <i className="bi bi-arrow-left me-2"></i> Back
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setCurrentStep("features")}>
              Continue <i className="bi bi-arrow-right ms-2"></i>
            </button>
          </>
        )}

        {currentStep === "features" && (
          <>
            <button type="button" className="btn btn-light" onClick={() => setCurrentStep("platform")}>
              <i className="bi bi-arrow-left me-2"></i> Back
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setCurrentStep("review")}>
              Continue <i className="bi bi-arrow-right ms-2"></i>
            </button>
          </>
        )}

        {currentStep === "review" && (
          <>
            <button type="button" className="btn btn-light" onClick={() => setCurrentStep("features")}>
              <i className="bi bi-arrow-left me-2"></i> Back
            </button>
            <button type="button" className="btn btn-primary" onClick={handleScaffold} disabled={isScaffolding}>
              <i className="bi bi-magic me-2"></i> Create MVP
            </button>
          </>
        )}

        {currentStep === "complete" && (
          <button type="button" className="btn btn-primary" onClick={handleClose}>
            Done
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

// Result card component
const ResultCard: React.FC<{
  title: string;
  result: ScaffoldResult;
  icon: string;
  onCopy: (command: string, type: string) => void;
  copied: boolean;
}> = ({ title, result, icon, onCopy, copied }) => (
  <div className="card mb-3">
    <div className="card-body">
      <div className="d-flex align-items-center mb-3">
        <i className={`bi ${icon} fs-3 text-primary me-2`}></i>
        <h6 className="fw-bold mb-0">{title}</h6>
      </div>

      <div className="mb-3">
        <label className="text-muted fs-7 d-block mb-1">Repository:</label>
        <a href={result.repoUrl} target="_blank" rel="noopener noreferrer" className="text-primary">
          {result.repoUrl}
          <i className="bi bi-box-arrow-up-right ms-1"></i>
        </a>
      </div>

      <div>
        <label className="text-muted fs-7 d-block mb-1">Clone command:</label>
        <div className="d-flex align-items-center bg-dark rounded p-2">
          <code className="text-light flex-grow-1 fs-7">{result.cloneCommand}</code>
          <button
            className="btn btn-sm btn-light ms-2"
            onClick={() => onCopy(result.cloneCommand, title.includes("Mobile") ? "expo" : "nextjs")}
          >
            {copied ? <i className="bi bi-check2 text-success"></i> : <i className="bi bi-clipboard"></i>}
          </button>
        </div>
      </div>
    </div>
  </div>
);

export default MVPScaffolderModal;
